import { Component, EventEmitter, Inject, Input, Optional, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TokenContentCertificate, TokenContentTokenDetails } from '../../core/services/local-agent.service';

export interface TokenContentResultModalData {
  message: string;
  certificates: TokenContentCertificate[];
  tokenLabel?: string;
  tokenDetails?: TokenContentTokenDetails | null;
}

@Component({
  selector: 'app-token-content-result-modal',
  templateUrl: './token-content-result-modal.component.html',
  styleUrls: ['./token-content-result-modal.component.css'],
  standalone: false
})
/** Índice de pestaña activa por certificado: 0 General, 1 Titular y emisor, 2 Técnico, 3 Token */
export class TokenContentResultModalComponent {
  activeTabs: number[] = [];

  /** Cuando true, se muestra en el espacio de trabajo (sin overlay). */
  @Input() embedded = false;
  @Input() embeddedData: TokenContentResultModalData | null = null;

  @Output() embeddedClose = new EventEmitter<void>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<TokenContentResultModalComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) public injectedData: TokenContentResultModalData | null
  ) {}

  get viewData(): TokenContentResultModalData {
    const d = this.embeddedData ?? this.injectedData;
    if (!d) {
      throw new Error('TokenContentResultModal: faltan datos.');
    }
    return d;
  }

  getActiveTab(certIndex: number): number {
    return this.activeTabs[certIndex] ?? 0;
  }

  setActiveTab(certIndex: number, tab: number): void {
    const next = [...this.activeTabs];
    next[certIndex] = tab;
    this.activeTabs = next;
  }

  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.embeddedClose.emit();
    }
  }

  displayName(cert: TokenContentCertificate): string {
    return cert.label || this.extractCN(cert.subject) || cert.thumbprint || 'Certificado';
  }

  extractCN(subject?: string): string | null {
    if (!subject) return null;
    const match = subject.match(/CN=([^,]+)/i);
    return match ? match[1].trim() : null;
  }

  extractIssuerName(issuer?: string): string {
    if (!issuer) return '';
    const cnMatch = issuer.match(/CN=([^,]+)/i);
    const oMatch = issuer.match(/O=([^,]+)/i);
    const cn = cnMatch ? cnMatch[1].trim() : '';
    const o = oMatch ? oMatch[1].trim() : '';
    if (cn && o) return `${cn} - ${o}`;
    return cn || o || issuer;
  }

  /**
   * Parsea un DN (Subject o Issuer) separando por ", " y cada parte en clave=valor.
   * Devuelve lista de { key, value } para mostrar en líneas.
   */
  parseDN(dn?: string): { key: string; value: string }[] {
    if (!dn || !dn.trim()) return [];
    const parts = dn.split(/,\s*/).map(p => p.trim()).filter(Boolean);
    const result: { key: string; value: string }[] = [];
    for (const part of parts) {
      const eq = part.indexOf('=');
      if (eq >= 0) {
        result.push({
          key: part.substring(0, eq).trim(),
          value: part.substring(eq + 1).trim()
        });
      } else {
        result.push({ key: '', value: part });
      }
    }
    return result;
  }

  formatSerial(serial?: string): string {
    if (!serial) return '';
    if (serial.length <= 8) return serial;
    return '...' + serial.slice(-8).toUpperCase();
  }

  formatDate(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-CO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }

  isExpired(notAfter?: string): boolean {
    if (!notAfter) return false;
    try {
      return new Date(notAfter) < new Date();
    } catch {
      return false;
    }
  }
}
